<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgentWizard from './components/AgentWizard.vue';

const store = useStore();
const { t } = useI18n();

const agents = computed(() => store.getters['aiAgents/getAgents'] || []);
const uiFlags = computed(() => store.getters['aiAgents/getUIFlags']);
const isLoading = computed(() => uiFlags.value.isFetching);

const showForm = ref(false);
const editingAgent = ref(null);
const editName = ref('');
const editDescription = ref('');
const isSaving = ref(false);
const agentToDelete = ref(null);
const isDeleting = ref(false);

const catalogLabels = {
  products: 'Productos',
  services: 'Servicios',
  vehicles: 'Vehículos',
  real_estate: 'Inmuebles',
};

const channelIcons = {
  'Channel::WebWidget': 'i-lucide-globe',
  'Channel::Api': 'i-lucide-webhook',
  'Channel::Whatsapp': 'i-lucide-message-circle',
  'Channel::FacebookPage': 'i-lucide-facebook',
  'Channel::Telegram': 'i-lucide-send',
  'Channel::Email': 'i-lucide-mail',
  'Channel::Sms': 'i-lucide-smartphone',
  'Channel::Line': 'i-lucide-message-square',
};

onMounted(() => {
  store.dispatch('aiAgents/get');
});

const openForm = () => {
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
};

const startEditing = agent => {
  editingAgent.value = agent.id;
  editName.value = agent.name;
  editDescription.value = agent.description || '';
};

const cancelEditing = () => {
  editingAgent.value = null;
};

const saveAgent = async agent => {
  if (!editName.value.trim()) return;
  isSaving.value = true;
  try {
    await store.dispatch('aiAgents/update', {
      id: agent.id,
      data: {
        name: editName.value.trim(),
        description: editDescription.value.trim(),
      },
    });
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
    editingAgent.value = null;
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const toggleStatus = async agent => {
  const newStatus = agent.status === 0 ? 1 : 0;
  try {
    await store.dispatch('aiAgents/update', {
      id: agent.id,
      data: { status: newStatus === 0 ? 'active' : 'inactive' },
    });
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_SUCCESS'));
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.UPDATE_ERROR'));
  }
};

const showDeleteModal = agent => {
  agentToDelete.value = agent;
};

const cancelDelete = () => {
  agentToDelete.value = null;
};

const confirmDelete = async () => {
  if (!agentToDelete.value) return;
  isDeleting.value = true;
  try {
    await store.dispatch('aiAgents/delete', agentToDelete.value.id);
    useAlert(t('AI_AGENTS.MESSAGES.DELETE_SUCCESS'));
  } catch {
    useAlert(t('AI_AGENTS.MESSAGES.DELETE_ERROR'));
  } finally {
    isDeleting.value = false;
    agentToDelete.value = null;
  }
};

const getChannelIcon = channelType => {
  return channelIcons[channelType] || 'i-lucide-inbox';
};
</script>

<template>
  <div class="flex flex-col w-full">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('AI_AGENTS.HEADER') }}
        </h1>
        <p class="text-sm text-n-slate-10 mt-1">
          {{ t('AI_AGENTS.DESCRIPTION') }}
        </p>
      </div>
      <button
        class="px-4 py-2.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90 flex items-center gap-2"
        @click="openForm"
      >
        <span class="i-lucide-plus w-4 h-4" />
        {{ t('AI_AGENTS.ADD') }}
      </button>
    </div>

    <!-- Loading -->
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-12 text-sm text-n-slate-10"
    >
      <span class="i-lucide-loader-2 w-5 h-5 animate-spin mr-2" />
      {{ t('AI_AGENTS.LOADING') }}
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!agents.length"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <div
        class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center mb-4"
      >
        <span class="i-lucide-bot w-8 h-8 text-n-slate-9" />
      </div>
      <p class="text-sm text-n-slate-10 mb-4">
        {{ t('AI_AGENTS.NO_RESULTS') }}
      </p>
      <button
        class="px-4 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:bg-n-brand/90"
        @click="openForm"
      >
        {{ t('AI_AGENTS.ADD') }}
      </button>
    </div>

    <!-- Agent Cards -->
    <div v-else class="grid gap-4">
      <div
        v-for="agent in agents"
        :key="agent.id"
        class="border border-n-slate-6 rounded-xl p-4 hover:border-n-slate-8 transition-colors bg-n-surface-1"
      >
        <div class="flex items-start justify-between gap-4">
          <!-- Left: Info -->
          <div class="flex-1 min-w-0">
            <!-- Editing mode -->
            <div v-if="editingAgent === agent.id" class="space-y-3">
              <input
                v-model="editName"
                class="w-full px-3 py-1.5 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none"
                :placeholder="t('AI_AGENTS.FORM.NAME_PLACEHOLDER')"
              />
              <textarea
                v-model="editDescription"
                rows="2"
                class="w-full px-3 py-1.5 text-sm rounded-lg border border-n-slate-6 bg-n-surface-2 text-n-slate-12 focus:border-n-brand focus:outline-none resize-none"
                :placeholder="t('AI_AGENTS.FORM.DESCRIPTION')"
              />
              <div class="flex items-center gap-2">
                <button
                  :disabled="isSaving"
                  class="px-3 py-1 text-xs font-medium rounded-md bg-n-brand text-white hover:bg-n-brand/90 disabled:opacity-50"
                  @click="saveAgent(agent)"
                >
                  {{ t('AI_AGENTS.TABLE.SAVE') }}
                </button>
                <button
                  class="px-3 py-1 text-xs font-medium rounded-md border border-n-slate-6 text-n-slate-11 hover:bg-n-slate-3"
                  @click="cancelEditing"
                >
                  {{ t('AI_AGENTS.TABLE.CANCEL') }}
                </button>
              </div>
            </div>

            <!-- Display mode -->
            <div v-else>
              <div class="flex items-center gap-2 mb-1">
                <h3 class="text-sm font-semibold text-n-slate-12 truncate">
                  {{ agent.name }}
                </h3>
                <button
                  class="text-n-slate-9 hover:text-n-slate-12 flex-shrink-0"
                  :title="t('AI_AGENTS.TABLE.EDIT')"
                  @click="startEditing(agent)"
                >
                  <span class="i-lucide-pencil w-3.5 h-3.5" />
                </button>
              </div>
              <p
                v-if="agent.description"
                class="text-xs text-n-slate-10 line-clamp-2 mb-2"
              >
                {{ agent.description }}
              </p>

              <!-- Tags row -->
              <div class="flex flex-wrap items-center gap-2 mt-2">
                <!-- Status toggle -->
                <button
                  class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
                  :class="
                    agent.status === 0
                      ? 'bg-n-teal-3 text-n-teal-11 hover:bg-n-teal-4'
                      : 'bg-n-slate-3 text-n-slate-10 hover:bg-n-slate-4'
                  "
                  :title="
                    agent.status === 0
                      ? t('AI_AGENTS.STATUSES.ACTIVE')
                      : t('AI_AGENTS.STATUSES.INACTIVE')
                  "
                  @click="toggleStatus(agent)"
                >
                  <span
                    class="w-1.5 h-1.5 rounded-full"
                    :class="agent.status === 0 ? 'bg-n-teal-9' : 'bg-n-slate-8'"
                  />
                  {{
                    agent.status === 0
                      ? t('AI_AGENTS.STATUSES.ACTIVE')
                      : t('AI_AGENTS.STATUSES.INACTIVE')
                  }}
                </button>

                <!-- Model badge -->
                <span
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-violet-3 text-n-violet-11"
                >
                  <span class="i-lucide-cpu w-3 h-3" />
                  {{ agent.llm_model }}
                </span>

                <!-- Catalog type badge -->
                <span
                  v-if="agent.catalog_type"
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-amber-3 text-n-amber-11"
                >
                  <span class="i-lucide-package w-3 h-3" />
                  {{ catalogLabels[agent.catalog_type] || agent.catalog_type }}
                </span>

                <!-- Inboxes badges -->
                <template v-if="agent.inboxes && agent.inboxes.length">
                  <span
                    v-for="inbox in agent.inboxes"
                    :key="inbox.id"
                    class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-blue-3 text-n-blue-11"
                  >
                    <span
                      :class="getChannelIcon(inbox.channel_type)"
                      class="w-3 h-3"
                    />
                    {{ inbox.name }}
                  </span>
                </template>
                <span
                  v-else
                  class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-n-ruby-3 text-n-ruby-11"
                >
                  <span class="i-lucide-alert-circle w-3 h-3" />
                  {{ t('AI_AGENTS.TABLE.NO_INBOXES') }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right: Actions -->
          <div class="flex items-center gap-1 flex-shrink-0">
            <router-link
              :to="{ name: 'ai_agent_show', params: { id: agent.id } }"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-slate-6 text-n-slate-11 hover:bg-n-slate-3 transition-colors"
            >
              <span class="i-lucide-settings w-3.5 h-3.5" />
              {{ t('AI_AGENTS.TABLE.CONFIGURE') }}
            </router-link>
            <button
              class="inline-flex items-center justify-center w-8 h-8 rounded-lg text-n-slate-9 hover:text-n-ruby-9 hover:bg-n-ruby-3 transition-colors"
              :title="t('AI_AGENTS.TABLE.DELETE')"
              @click="showDeleteModal(agent)"
            >
              <span class="i-lucide-trash-2 w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div
      v-if="agentToDelete"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="cancelDelete"
    >
      <div class="w-full max-w-md rounded-xl bg-n-surface-1 shadow-2xl p-6">
        <div class="flex items-start gap-4 mb-5">
          <div
            class="w-10 h-10 rounded-full bg-n-ruby-3 flex items-center justify-center flex-shrink-0"
          >
            <span class="i-lucide-trash-2 w-5 h-5 text-n-ruby-9" />
          </div>
          <div>
            <h3 class="text-base font-semibold text-n-slate-12 mb-1">
              {{ t('AI_AGENTS.TABLE.DELETE') }}
            </h3>
            <p class="text-sm text-n-slate-10">
              {{ t('AI_AGENTS.TABLE.CONFIRM_DELETE') }}
            </p>
          </div>
        </div>

        <div
          class="flex items-center gap-2 p-3 rounded-lg bg-n-slate-2 border border-n-slate-6 mb-5"
        >
          <span class="i-lucide-bot w-4 h-4 text-n-slate-9" />
          <span class="text-sm font-medium text-n-slate-12">
            {{ agentToDelete.name }}
          </span>
        </div>

        <div class="flex items-center justify-end gap-3">
          <button
            class="px-4 py-2 text-sm font-medium rounded-lg border border-n-slate-6 text-n-slate-11 hover:bg-n-slate-3 transition-colors"
            @click="cancelDelete"
          >
            {{ t('AI_AGENTS.TABLE.CANCEL') }}
          </button>
          <button
            :disabled="isDeleting"
            class="px-4 py-2 text-sm font-medium rounded-lg bg-n-ruby-9 text-white hover:bg-n-ruby-10 transition-colors disabled:opacity-50 flex items-center gap-2"
            @click="confirmDelete"
          >
            <span
              v-if="isDeleting"
              class="i-lucide-loader-2 w-4 h-4 animate-spin"
            />
            <span v-else class="i-lucide-trash-2 w-4 h-4" />
            {{ t('AI_AGENTS.TABLE.DELETE') }}
          </button>
        </div>
      </div>
    </div>

    <AgentWizard :show="showForm" @close="closeForm" />
  </div>
</template>
